
import {
  defineComponent,
  computed,
  PropType,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch
} from 'vue'
import {
  getStageByScore,
  getThermoArrowSrc,
  getThermoEmptyTrackSrc,
  roundSentimentScore,
  formatTradingDay,
  THERMO_GRADIENT_CSS
} from '@/utils/sentiment/constants'
import type { SentimentThemeMode } from '@/utils/sentiment/constants'
import { getStageTierTextColor } from '@/utils/sentiment/theme'

/** 底图原始尺寸 117×349，以下坐标按像素标定后换算为百分比 */
const DESIGN_W = 117
const DESIGN_H = 349
const TUBE = { left: 50, top: 40, width: 18, height: 269 }
const ARROW = { left: 68, width: 18, height: 18 }
/** 面板内展示高度（等比缩放，底图 117×349）；XP 等旧内核不支持 aspect-ratio，用固定像素兜底 */
const BASE_GAUGE_H = 225
const BASE_GAUGE_W = Math.round((DESIGN_W / DESIGN_H) * BASE_GAUGE_H)

function pctX(px: number) {
  return `${(px / DESIGN_W) * 100}%`
}

function pctY(px: number) {
  return `${(px / DESIGN_H) * 100}%`
}

export default defineComponent({
  name: 'SentimentThermometerPanel',
  props: {
    score: { type: Number, default: 0 },
    tradingDay: { type: Number, default: 0 },
    theme: { type: String as PropType<SentimentThemeMode>, default: 'dark' },
    fontScale: { type: Number, default: 1 }
  },
  setup(props) {
    const wrapRef = ref<HTMLElement | null>(null)
    const measuredGauge = ref<{ w: number; h: number } | null>(null)
    let resizeTimer: ReturnType<typeof setTimeout> | null = null

    const measureGaugeSize = () => {
      const el = wrapRef.value
      if (!el) return
      const footer = el.querySelector('.thermo-footer') as HTMLElement | null
      const updateTime = el.querySelector('.thermo-update-time') as HTMLElement | null
      const reserved =
        (footer?.offsetHeight ?? 0) +
        (updateTime?.offsetHeight ?? 0) +
        Math.round(12 * (props.fontScale > 0 ? props.fontScale : 1))
      const availH = el.clientHeight - reserved
      const availW = el.clientWidth
      if (availH < 48 || availW < 24) return

      const ratio = DESIGN_W / DESIGN_H
      let h = availH
      let w = Math.round(h * ratio)
      if (w > availW) {
        w = availW
        h = Math.round(w / ratio)
      }
      measuredGauge.value = { w, h }
    }

    const scheduleMeasure = () => {
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        resizeTimer = null
        nextTick(measureGaugeSize)
      }, 50)
    }

    const displayScore = computed(() => roundSentimentScore(props.score))
    const updateTimeText = computed(() =>
      props.tradingDay ? formatTradingDay(props.tradingDay) : '--'
    )
    const stage = computed(() => getStageByScore(displayScore.value))
    const fillPercent = computed(() =>
      Math.max(0, Math.min(100, displayScore.value))
    )
    const accentColor = computed(() =>
      getStageTierTextColor(stage.value.tier)
    )
    const arrowSrc = computed(() => getThermoArrowSrc(stage.value.tier))
    const emptyTrackSrc = computed(() => getThermoEmptyTrackSrc(props.theme))

    const hasFill = computed(() => fillPercent.value > 0)

    const tubeStyle = computed(() => ({
      left: pctX(TUBE.left),
      width: pctX(TUBE.width),
      top: pctY(TUBE.top),
      height: pctY(TUBE.height)
    }))

    const fillStyle = computed(() => {
      const pct = fillPercent.value
      const gradient = THERMO_GRADIENT_CSS
      const webkitGradient = gradient.replace(
        'linear-gradient(',
        '-webkit-linear-gradient('
      )
      return {
        height: `${pct}%`,
        backgroundImage: gradient,
        WebkitBackgroundImage: webkitGradient,
        backgroundSize: pct > 0 ? `100% ${(100 / pct) * 100}%` : '100% 100%',
        backgroundPosition: 'bottom center'
      }
    })

    const resolveGaugeSize = () => {
      const scale = props.fontScale > 0 ? props.fontScale : 1
      const gaugeH = measuredGauge.value?.h ?? Math.round(BASE_GAUGE_H * scale)
      const gaugeW = measuredGauge.value?.w ?? Math.round(BASE_GAUGE_W * scale)
      return { gaugeH, gaugeW }
    }

    const arrowStyle = computed(() => {
      // 液面从管底向上长，顶部 Y = 管顶 + 未填充高度
      const fillTopPx =
        TUBE.top + TUBE.height - (fillPercent.value / 100) * TUBE.height
      const { gaugeH } = resolveGaugeSize()
      // 按底图等比缩放，宽高同值保持箭头不变形（勿用 width% 同时设 height%）
      const arrowPx = Math.max(12, Math.round(ARROW.width * (gaugeH / DESIGN_H)))
      return {
        left: pctX(ARROW.left),
        top: pctY(fillTopPx),
        width: `${arrowPx}px`,
        height: `${arrowPx}px`
      }
    })

    const pillStyle = computed(() => ({
      color: accentColor.value,
      borderColor: accentColor.value
    }))

    const gaugeStyle = computed(() => {
      const scale = props.fontScale > 0 ? props.fontScale : 1
      const fallbackH = Math.round(BASE_GAUGE_H * scale)
      const fallbackW = Math.round(BASE_GAUGE_W * scale)
      const gaugeH = measuredGauge.value?.h ?? fallbackH
      const gaugeW = measuredGauge.value?.w ?? fallbackW
      return {
        width: `${gaugeW}px`,
        height: `${gaugeH}px`,
        maxHeight: '100%',
        '--tube-left': pctX(TUBE.left),
        '--tube-width': pctX(TUBE.width),
        '--tube-top': pctY(TUBE.top),
        '--tube-height': pctY(TUBE.height)
      }
    })

    onMounted(() => {
      scheduleMeasure()
      setTimeout(scheduleMeasure, 120)
      setTimeout(scheduleMeasure, 400)
      window.addEventListener('resize', scheduleMeasure)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', scheduleMeasure)
      if (resizeTimer) clearTimeout(resizeTimer)
    })

    watch(
      () => props.fontScale,
      () => scheduleMeasure()
    )

    const wrapStyle = {
      width: '100%'
    }

    return {
      wrapRef,
      emptyTrackSrc,
      gaugeStyle,
      tubeStyle,
      wrapStyle,
      displayScore,
      stage,
      hasFill,
      fillStyle,
      arrowStyle,
      accentColor,
      arrowSrc,
      pillStyle,
      updateTimeText
    }
  }
})
